import { GrayButton } from 'components/Button'
import Column from 'components/Column'
import CopyToClipboard from 'components/CopyToClipboard'
import Row from 'components/Row'
import Text from 'components/Text'
import { selectors } from 'data'
import { ApiSettingsType } from 'data/types'
import messages from 'lang'
import borderRadius from 'polished/lib/shorthands/borderRadius'
import { QRCodeCanvas } from 'qrcode.react'
import React from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { color, screenWidth, theme } from 'utils'

const Wrapper = styled(Column)`
  @media only screen and (min-width: ${screenWidth('sm')}) {
    width: 540px;
  }
`
const Title = styled(Text)`
  display: block;
  margin-bottom: 2rem;
  text-align: center;
`
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  flex: 1;
  padding: 2rem 2.5rem;
  background: ${color('grey900')};
  border-radius: ${theme('inputBorderRadius')};
  ${borderRadius('bottom', 0)};

  @media only screen and (max-width: ${screenWidth('sm')}) {
    padding: 1.5rem;
    border-radius: 0;
  }
`
const BodyWrapper = styled(HeaderWrapper)`
  background: #fff;
  border-radius: ${theme('inputBorderRadius')};
  ${borderRadius('top', 0)};
`
const Label = styled(Text)`
  margin-bottom: 0.75rem;
`
const SubText = styled(Text)`
  margin-bottom: 1rem;
  align-self: flex-start;
  font-size: 0.8em;
`

const ActivationText = styled(SubText)`
  margin-bottom: 0;
`
const IdRow = styled(Row)`
  border-radius: 4px;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #fff;
  width: 100%;

  & span {
    color: #121d33;
  }
`
const SecretIdRow = styled(IdRow)`
  align-items: flex-start;
  background: ${color('grey000')};

  & span {
    padding-right: 30px;
    word-break: break-all;
    font-size: 0.65rem;
  }
`
const QrContainer = styled(Column)`
  width: 100%;
  align-items: center;
  border-top: 2px solid ${color('dark000')};
  margin-top: 20px;
  padding-top: 20px;
`
const CloseButton = styled(GrayButton)`
  align-self: flex-end;
`

type PropsType = {
  api: ApiSettingsType
  hideSuccessModal: (e: React.SyntheticEvent<any>) => void
  showApiActivationToast: () => void
} & WrappedComponentProps

const Success = ({ api, hideSuccessModal, intl, showApiActivationToast }: PropsType) => {
  const apiKey = api && api.newKey && api.newKey.keyId
  const apiToken = api && api.newKey && api.newKey.token
  const qrCodeDataString = JSON.stringify({ apiKey, secretKey: apiToken })
  const email = useSelector(selectors.auth.getEmail)
  return (
    <Wrapper>
      <HeaderWrapper>
        <Title color='white' size='xxl' weight={600}>
          {intl.formatMessage(messages.api_key_success)}
        </Title>
        <Label color='white' size='xs' weight={600}>
          {intl.formatMessage(messages.api_key_new, { email })}
        </Label>
        <IdRow>
          <Text textTransform='none'>{apiKey || 'none'}</Text>
          <CopyToClipboard customHandler={showApiActivationToast} value={apiKey || 'none'} />
        </IdRow>
      </HeaderWrapper>
      <BodyWrapper>
        <ActivationText color='black'>
          {intl.formatMessage(messages.api_key_activation)}
        </ActivationText>

        <SubText color='black'>
          {intl.formatMessage(messages.api_key_never_reshow_part1)}
          <strong> {intl.formatMessage(messages.api_key_never_reshow_part2)} </strong>
          {intl.formatMessage(messages.api_key_never_reshow_part3)}
        </SubText>
        <Label color='grey600' size='xs' weight={600}>
          {intl.formatMessage(messages.api_key_secret)}
        </Label>
        <SecretIdRow>
          <Text textTransform='none'>{apiToken || 'none'}</Text>
          <CopyToClipboard customHandler={showApiActivationToast} value={apiToken || 'none'} />
        </SecretIdRow>
        <QrContainer>
          <SubText color='black'>{intl.formatMessage(messages.api_key_activation_qr_code)}</SubText>
          <QRCodeCanvas level='M' size={160} value={qrCodeDataString} />
        </QrContainer>
        <CloseButton e2e='closeButton' onClick={hideSuccessModal} type='button'>
          {intl.formatMessage(messages.button_close)}
        </CloseButton>
      </BodyWrapper>
    </Wrapper>
  )
}

export default injectIntl(Success)
